import type {
  Annotation,
  ApiKeyCreated,
  ApiKeySummary,
  AppSetting,
  AutoSegParamsSchema,
  ActivityFeedPage,
  ArtifactReviewContext,
  AuditPage,
  BulkStartResult,
  CaseSummary,
  CaseTimelineEntry,
  ChecklistState,
  Collection,
  CollectionPatch,
  CollectionType,
  ExportJob,
  HealthStatus,
  IngestPendingReviewItem,
  IngestStatus,
  Me,
  NewCollection,
  QASamplePreview,
  QASampleResult,
  RoutingPreview,
  SearchFilters,
  SearchResponse,
  SegmentCatalog,
  StartStudyResult,
  StudyListFilters,
  StudyListResponse,
  StudyMatrix,
  StudyMetadataPatch,
  StudyVersionsResponse,
  Task,
  TaskKind,
  ThreadEntry,
  UserAnnotationPage,
  UserSummary,
  UserWorkPage,
  WorkflowEditorSchema,
  WorkflowGraph,
  WorkflowProfile,
  WorkflowRun,
} from './types'

class ApiError extends Error {
  public requestId?: string
  constructor(public status: number, message: string, requestId?: string) {
    super(message)
    this.name = 'ApiError'
    this.requestId = requestId
  }
}

/** A readable message out of FastAPI's `detail`, whatever shape it is.
 *
 *  `detail` is a plain string for `HTTPException(detail="…")`, but a *list* for
 *  request-validation 422s and an *object* for our own structured ones (the
 *  routing validator raises `{key, errors: [...]}`). Passing those to
 *  `new Error()` renders "[object Object]" in the toast — the maintainer sees a
 *  red box that names neither the field nor the reason. `fallback` is the HTTP
 *  status, not `resp.statusText`: fetch leaves statusText empty on its own
 *  Responses, which would render an empty toast.
 */
function errorMessage(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail)) {
    // Pydantic 422: [{loc: [...], msg: "…"}]. `loc` locates the bad field.
    const lines = detail.map((d) => {
      if (typeof d === 'string') return d
      const { loc, msg } = (d ?? {}) as { loc?: unknown; msg?: unknown }
      const where = Array.isArray(loc) ? loc.filter((p) => p !== 'body').join('.') : ''
      return [where, msg].filter(Boolean).join(': ') || JSON.stringify(d)
    })
    return lines.join('\n') || fallback
  }
  if (detail && typeof detail === 'object') {
    const errors = (detail as { errors?: unknown }).errors
    if (Array.isArray(errors) && errors.length) return errorMessage(errors, fallback)
    return JSON.stringify(detail)
  }
  return fallback
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    credentials: 'include',
  })
  const requestId = resp.headers.get('x-request-id') ?? undefined
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}))
    throw new ApiError(resp.status, errorMessage(body.detail, `HTTP ${resp.status}`), requestId)
  }
  if (resp.status === 204) return undefined as T
  return resp.json() as Promise<T>
}

/** 0→1 as the request body goes out. Never called past 1, never called at all
 *  if the browser can't compute a total. */
export type UploadProgress = (fraction: number) => void

/**
 * XHR, not `fetch` — `fetch` has no upload-progress event, and a DICOM archive
 * is big enough that "Uploading…" with no bar is the difference between waiting
 * and reloading the page. Error handling mirrors `request()` exactly: same
 * `x-request-id` capture, same `errorMessage(detail)` unwrapping.
 */
function multipartRequest<T>(
  path: string,
  formData: FormData,
  onProgress?: UploadProgress,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `/api${path}`)
    xhr.withCredentials = true
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total)
      }
    }
    xhr.onload = () => {
      const requestId = xhr.getResponseHeader('x-request-id') ?? undefined
      let body: { detail?: unknown } = {}
      try {
        body = JSON.parse(xhr.responseText)
      } catch {
        /* non-JSON body — fall through to the status-code message */
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(xhr.status, errorMessage(body.detail, `HTTP ${xhr.status}`), requestId))
        return
      }
      resolve(body as T)
    }
    xhr.onerror = () => reject(new ApiError(0, 'Network error during upload'))
    xhr.onabort = () => reject(new ApiError(0, 'Upload cancelled'))
    xhr.send(formData)
  })
}

export const apiClient = {
  // ---- Auth ----------------------------------------------------------------
  getMe: (): Promise<Me> => request<Me>('/auth/me'),
  logout: (): Promise<{ redirect_url: string }> =>
    request<{ redirect_url: string }>('/auth/logout', { method: 'POST', body: '{}' }),

  // ---- Tasks ---------------------------------------------------------------
  fetchTasks: (filters?: { study_uid?: string; task_kind?: TaskKind }): Promise<Task[]> => {
    const params = new URLSearchParams()
    if (filters?.study_uid) params.set('study_uid', filters.study_uid)
    if (filters?.task_kind) params.set('task_kind', filters.task_kind)
    const qs = params.toString() ? `?${params}` : ''
    return request<Task[]>(`/tasks${qs}`)
  },

  createTask: (opts: {
    study_uid: string
    title: string
    description?: string
    requires_review?: boolean
    instructions?: string
    max_annotators?: number
    task_kind?: TaskKind
    organs?: string[]
  }): Promise<Task> =>
    request<Task>('/tasks', {
      method: 'POST',
      body: JSON.stringify(opts),
    }),

  bulkCreateTasks: (opts: {
    study_uids: string[]
    title_prefix?: string
    requires_review?: boolean
    assignee_id?: string
    instructions?: string
    max_annotators?: number
    task_kind?: TaskKind
    organs?: string[]
  }): Promise<Task[]> =>
    request<Task[]>('/tasks/bulk', {
      method: 'POST',
      body: JSON.stringify(opts),
    }),

  // Self-claim (technician omits assignee_id)
  claimTask: (id: string): Promise<void> =>
    request<void>(`/tasks/${id}/assign`, { method: 'POST', body: '{}' }),

  /**
   * Hand a claim back to the pool. The gateway picks the caller's slot, compacts
   * a dual-reader row so the freed slot is actually offered again, and 409s if
   * the work is already submitted.
   *
   * `slot` is only needed when a maintainer force-releases a row with BOTH slots
   * filled and has to say which one.
   */
  releaseTask: (id: string, slot?: 1 | 2): Promise<{ slot: number; status: string }> =>
    request(`/tasks/${id}/release`, {
      method: 'POST',
      body: JSON.stringify(slot ? { slot } : {}),
    }),

  // Direct assign (maintainer provides assignee_id)
  assignTask: (id: string, assigneeId: string): Promise<void> =>
    request<void>(`/tasks/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assignee_id: assigneeId }),
    }),

  heartbeat: (id: string): Promise<void> =>
    request<void>(`/tasks/${id}/heartbeat`, { method: 'POST', body: '{}' }),

  submitAnnotation: (
    id: string,
    annotationPayload: Record<string, unknown>,
  ): Promise<{ ok: boolean; annotation_id: string }> =>
    request(`/tasks/${id}/submit`, {
      method: 'POST',
      // No `esignature`: the signature step is gone (see SubmitDialog). The
      // gateway keeps the field optional and the column intact for historical
      // rows, so omitting it is the whole client-side change.
      body: JSON.stringify({ annotation_payload: annotationPayload }),
    }),

  /**
   * Record a reviewer verdict. Returns how many verdicts this round now has and
   * how many it needs: a mask review waits for N DISTINCT reviewers, so the
   * caller has to be able to say "recorded, waiting for one more" rather than
   * implying the decision took effect.
   */
  reviewTask: (
    id: string,
    decision: 'approved' | 'reject',
    remark?: string,
  ): Promise<{ ok: boolean; verdicts_in: number; reviewers_required: number }> =>
    request(`/tasks/${id}/review`, {
      method: 'POST',
      body: JSON.stringify({ decision, remark }),
    }),

  // The full review conversation for a study: radiologist remarks, technician
  // notes, and the maintainer's reason, merged across every task row.
  getStudyThread: (studyUid: string): Promise<ThreadEntry[]> =>
    request<ThreadEntry[]>(`/tasks/study/${encodeURIComponent(studyUid)}/thread`),

  maintainerDecision: (
    id: string,
    decision: 'override_approve' | 'quarantine' | 'picked',
    opts?: { winner_annotation_id?: string; remark?: string },
  ): Promise<void> =>
    request<void>(`/tasks/${id}/maintainer-decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, ...opts }),
    }),

  /** Start correcting one of two disagreeing reads: copies its mask into the caller's own
   *  anomaly draft, which the ordinary anomaly viewer then opens. Replaces any earlier draft. */
  startTiebreakEdit: (
    taskId: string,
    annotationId: string,
  ): Promise<{ ok: boolean; study_uid: string; annotation_id: string }> =>
    request(`/tasks/${taskId}/tiebreak-edit/start`, {
      method: 'POST',
      body: JSON.stringify({ annotation_id: annotationId }),
    }),

  /** Settle the disagreement with the caller's saved, edited version of a read. */
  submitTiebreakEdit: (
    taskId: string,
    sourceAnnotationId: string,
    remark?: string,
  ): Promise<{ ok: boolean; annotation_id: string }> =>
    request(`/tasks/${taskId}/tiebreak-edit/submit`, {
      method: 'POST',
      body: JSON.stringify({ source_annotation_id: sourceAnnotationId, remark }),
    }),

  // Annotations sub-endpoint
  getTaskAnnotations: (taskId: string): Promise<Annotation[]> =>
    request<Annotation[]>(`/tasks/${taskId}/annotations`),

  // ---- Dashboard (admin + maintainer) --------------------------------------
  /** `onDate` (ISO `YYYY-MM-DD`) narrows to one calendar day; `dateFrom`/`dateTo`
   *  narrow to an explicit range and win over both `onDate` and `windowDays`;
   *  `allTime` wins over all three — see `routers/dashboard.py`. */
  getUserWork: (
    windowDays = 30,
    onDate?: string,
    dateFrom?: string,
    dateTo?: string,
    allTime?: boolean,
  ): Promise<UserWorkPage> =>
    request<UserWorkPage>(
      `/dashboard/users?window_days=${windowDays}` +
        (onDate ? `&on_date=${encodeURIComponent(onDate)}` : '') +
        (dateFrom ? `&date_from=${encodeURIComponent(dateFrom)}` : '') +
        (dateTo ? `&date_to=${encodeURIComponent(dateTo)}` : '') +
        (allTime ? '&all_time=true' : ''),
    ),

  /**
   * Everything one person has filed, newest first — the `/dashboard` drill-down.
   *
   * Deliberately takes no window: the question is whether this annotator is
   * doing a good job, and a 30-day slice of that is a different question. Paged
   * instead (`limit` caps at 200 server-side). Same `dashboard:read` gate as
   * `getUserWork`, which is why the page is a child of `/dashboard`.
   */
  getUserAnnotations: (
    userId: string,
    opts?: { limit?: number; offset?: number; taskKind?: string; studyUid?: string },
  ): Promise<UserAnnotationPage> => {
    const p = new URLSearchParams()
    if (opts?.limit) p.set('limit', String(opts.limit))
    if (opts?.offset) p.set('offset', String(opts.offset))
    if (opts?.taskKind) p.set('task_kind', opts.taskKind)
    if (opts?.studyUid) p.set('study_uid', opts.studyUid)
    const qs = p.toString()
    return request<UserAnnotationPage>(
      `/dashboard/users/${encodeURIComponent(userId)}/annotations${qs ? `?${qs}` : ''}`,
    )
  },

  getStudyMatrix: (collectionId?: string): Promise<StudyMatrix> =>
    request<StudyMatrix>(
      `/dashboard/studies${collectionId ? `?collection_id=${encodeURIComponent(collectionId)}` : ''}`,
    ),

  /** Keyset-paginated, deliberately un-counted — a feed is scroll-back. */
  getActivityFeed: (opts?: {
    limit?: number
    before_ts?: string
    before_id?: string
    action?: string
    actor_user_id?: string
  }): Promise<ActivityFeedPage> => {
    const p = new URLSearchParams()
    if (opts?.limit) p.set('limit', String(opts.limit))
    if (opts?.before_ts) p.set('before_ts', opts.before_ts)
    if (opts?.before_id) p.set('before_id', opts.before_id)
    if (opts?.action) p.set('action', opts.action)
    if (opts?.actor_user_id) p.set('actor_user_id', opts.actor_user_id)
    const qs = p.toString()
    return request<ActivityFeedPage>(`/dashboard/activity${qs ? `?${qs}` : ''}`)
  },

  // ---- Studies -------------------------------------------------------------
  searchStudies: (
    filters: SearchFilters,
    page = 1,
    pageSize = 20,
  ): Promise<SearchResponse> => {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined) params.set(k, String(v))
    }
    params.set('page', String(page))
    params.set('page_size', String(pageSize))
    return request<SearchResponse>(`/studies/search?${params}`)
  },

  skipStudy: (studyUid: string): Promise<{ ok: boolean; study_uid: string }> =>
    request(`/studies/${studyUid}/skip`, { method: 'POST', body: '{}' }),

  /**
   * Study management list, straight from `study_status` — NOT OpenSearch.
   * `seg_state` changes many times per study and the index is
   * ingestion-owned, so live workflow state has to come from Postgres.
   */
  listStudies: (
    filters: StudyListFilters = {},
    limit = 50,
    offset = 0,
  ): Promise<StudyListResponse> => {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) {
      if (v !== undefined && v !== '') params.set(k, String(v))
    }
    params.set('limit', String(limit))
    params.set('offset', String(offset))
    return request<StudyListResponse>(`/studies?${params}`)
  },

  /** Every saved stage of a study's mask work, with the user who owns each and
   *  the SEG series to replay it from. Gated on `mask:versions_read`
   *  (maintainer + admin), so only call it behind `can('mask:versions_read')` —
   *  everyone else gets a 403. An empty `items` means nothing has been saved
   *  yet, which the picker renders as "NA". */
  listStudyVersions: (studyUid: string): Promise<StudyVersionsResponse> =>
    request<StudyVersionsResponse>(
      `/studies/${encodeURIComponent(studyUid)}/versions`,
    ),

  /** `job_description` is the maintainer's brief; it lands on every task card
   *  for the study. Optional on the wire so the SDK can start without one.
   *
   *  `profileId` is which workflow to run. Also optional on the wire: the gateway falls
   *  back to the study's collection standing value, then to the platform default, so an
   *  SDK start with no body still runs something deliberate. */
  startStudy: (
    studyUid: string,
    jobDescription?: string,
    profileId?: string,
    /** Account ids to send the study's tickets to. Empty / absent means everyone in the
     *  role, which is what every start did before this existed. */
    assignees?: { technician_ids?: string[]; radiologist_ids?: string[] },
  ): Promise<StartStudyResult> =>
    request(`/studies/${encodeURIComponent(studyUid)}/start`, {
      method: 'POST',
      body: JSON.stringify({
        job_description: jobDescription ?? null,
        profile_id: profileId ?? null,
        technician_ids: assignees?.technician_ids ?? [],
        radiologist_ids: assignees?.radiologist_ids ?? [],
      }),
    }),

  /** Who may claim a started study's tickets. Empty lists mean everyone in the role. */
  getStudyAssignees: (
    studyUid: string,
  ): Promise<{ technician_ids: string[]; radiologist_ids: string[] }> =>
    request(`/studies/${encodeURIComponent(studyUid)}/assignees`),

  /** Replace both lists on a study that is already running. No mask is touched; the
   *  gateway re-validates ids, roles and the workflow's minimum like a start does. */
  setStudyAssignees: (
    studyUid: string,
    assignees: { technician_ids: string[]; radiologist_ids: string[] },
  ): Promise<{ technician_ids: string[]; radiologist_ids: string[] }> =>
    request(`/studies/${encodeURIComponent(studyUid)}/assignees`, {
      method: 'PUT',
      body: JSON.stringify(assignees),
    }),

  /** Back to freshly-ingested, with every mask deleted. `confirm` must be
   *  `reset <study_uid>`; the gateway 422s anything else, so the dialog's typed
   *  phrase is the same string and not a client-only courtesy. */
  resetStudy: (studyUid: string): Promise<{ ok: boolean; study_uid: string }> =>
    request(
      `/studies/${encodeURIComponent(studyUid)}/reset` +
        `?confirm=${encodeURIComponent(`reset ${studyUid}`)}`,
      { method: 'POST', body: '{}' },
    ),

  /** Purge: Orthanc study, masks, annotations, Postgres rows, search doc. */
  deleteStudy: (studyUid: string): Promise<{ ok: boolean; study_uid: string }> =>
    request(
      `/studies/${encodeURIComponent(studyUid)}` +
        `?confirm=${encodeURIComponent(`delete ${studyUid}`)}`,
      { method: 'DELETE' },
    ),

  /** Maintainer's call on an auto-seg failure. Not a clinical decision — see
   *  the TASK_ASSIGN gate on the endpoint. */
  segFailureDecision: (
    studyUid: string,
    decision: 'retry' | 'proceed' | 'quarantine',
  ): Promise<{ ok: boolean; study_uid: string; decision: string }> =>
    request(`/studies/${encodeURIComponent(studyUid)}/seg-failure-decision`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    }),

  updateStudyMetadata: (
    studyUid: string,
    patch: StudyMetadataPatch,
  ): Promise<{ ok: boolean; study_uid: string; changed: string[] }> =>
    request(`/studies/${encodeURIComponent(studyUid)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  resetSegmentation: (
    studyUid: string,
  ): Promise<{ ok: boolean; study_uid: string; deleted: number }> =>
    request(`/segmentations/${encodeURIComponent(studyUid)}`, { method: 'DELETE' }),

  // ---- Exports -------------------------------------------------------------
  startExport: (studyUid: string, format = 'dicom'): Promise<ExportJob> =>
    request<ExportJob>('/exports', {
      method: 'POST',
      body: JSON.stringify({ study_uid: studyUid, format }),
    }),

  getExportStatus: (workflowId: string): Promise<ExportJob> =>
    request<ExportJob>(`/exports/${workflowId}`),

  // ---- Users ---------------------------------------------------------------
  listUsers: (role?: string): Promise<UserSummary[]> => {
    const qs = role ? `?role=${encodeURIComponent(role)}` : ''
    return request<UserSummary[]>(`/users${qs}`)
  },

  // ---- API keys (admin only) -----------------------------------------------
  listApiKeys: (includeRevoked = false): Promise<ApiKeySummary[]> =>
    request<ApiKeySummary[]>(`/api-keys${includeRevoked ? '?include_revoked=true' : ''}`),

  // The response is the ONLY time the plaintext key exists outside the caller's
  // hands — the gateway stores a SHA-256 and no endpoint returns it again.
  createApiKey: (body: {
    name: string
    owner_id: string
    expires_in_days: number
  }): Promise<ApiKeyCreated> =>
    request<ApiKeyCreated>('/api-keys', { method: 'POST', body: JSON.stringify(body) }),

  revokeApiKey: (keyId: string): Promise<ApiKeySummary> =>
    request<ApiKeySummary>(`/api-keys/${keyId}`, { method: 'DELETE' }),

  // ---- Per-case activity (dashboard:read = admin + maintainer) -------------
  // What is happening on each case, rather than what the platform did most
  // recently. `GET /dashboard/activity` still answers the second question.
  listCases: (windowDays = 30, limit = 50): Promise<CaseSummary[]> =>
    request<CaseSummary[]>(`/dashboard/cases?window_days=${windowDays}&limit=${limit}`),

  // Events + existing thread + posted comments, merged, NEWEST FIRST — the
  // opposite of getStudyThread, which reads top-down like a conversation.
  getCaseTimeline: (studyUid: string, limit = 200): Promise<CaseTimelineEntry[]> =>
    request<CaseTimelineEntry[]>(
      `/dashboard/cases/${encodeURIComponent(studyUid)}/timeline?limit=${limit}`,
    ),

  // Needs `case:comment` (maintainer + admin).
  addCaseComment: (studyUid: string, body: string): Promise<CaseTimelineEntry> =>
    request<CaseTimelineEntry>(`/dashboard/cases/${encodeURIComponent(studyUid)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  // ---- Audit ---------------------------------------------------------------
  fetchAudit: (
    filters: {
      actor_user_id?: string
      action?: string
      resource_type?: string
      resource_id?: string
      ts_from?: string
      ts_to?: string
    },
    page = 1,
    pageSize = 50,
  ): Promise<AuditPage> => {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) {
      if (v) params.set(k, v)
    }
    params.set('page', String(page))
    params.set('page_size', String(pageSize))
    return request<AuditPage>(`/audit?${params}`)
  },

  downloadAuditExport: (filters: Record<string, string>, format: 'csv' | 'jsonl'): void => {
    const params = new URLSearchParams({ format, ...filters }).toString()
    window.location.assign(`/api/audit/export?${params}`)
  },

  // ---- Settings ------------------------------------------------------------
  getSettings: (): Promise<AppSetting[]> =>
    request<AppSetting[]>('/settings'),

  getSetting: (key: string): Promise<AppSetting> =>
    request<AppSetting>(`/settings/${key}`),

  putSetting: (key: string, value: unknown): Promise<AppSetting> =>
    request<AppSetting>(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),

  // Every Auto3DSeg inference parameter: tier, bounds, default, help text, the
  // stored value, and each model's trained values. The registry is server-owned
  // (autoseg_params.py) so the Settings tab renders generically — see
  // AutoSegParamsCard. Unlike the segment catalog this does NOT 503 when MONAI
  // is unreachable; only the per-model column degrades.
  getAutoSegParamsSchema: (): Promise<AutoSegParamsSchema> =>
    request<AutoSegParamsSchema>('/settings/segmentation.autoseg-params/schema'),

  // Which auto-seg models would run for a (modality, region) pair. The fallback
  // chain lives server-side in seg_routing.py — don't reimplement it here.
  resolveModelRouting: (modality: string, bodyPart: string): Promise<RoutingPreview> => {
    const params = new URLSearchParams({ modality, body_part: bodyPart })
    return request<RoutingPreview>(`/settings/segmentation.model_routing/resolve?${params}`)
  },

  // Every model MONAI Label serves and the exact segment names each emits.
  // Read live from its /info/ — never mirrored into app_settings, because a
  // model's names change on its first download and a stale copy would offer
  // segments the model can't produce. 503s when MONAI is unreachable.
  getSegmentCatalog: (): Promise<SegmentCatalog> =>
    request<SegmentCatalog>('/settings/segmentation.catalog'),

  // ---- Ingest (admin dev tool) ---------------------------------------------
  // Accepts a single archive (.zip/.tar.gz/.tgz/.tar) OR one or more .dcm files.
  // Archives are sent as the 'file' field; individual .dcm files use 'files[]'.
  ingestDicom: (
    input: File | File[],
    opts: {
      collection_id: string
      // Mandatory server-side: decides whether the anomaly track ever opens.
      health_status: HealthStatus
      modalities?: string[]
      body_parts?: string[]
      disease?: string[]
    },
    onProgress?: UploadProgress,
  ): Promise<{ workflow_id: string; study_uid: string; warnings?: string[] }> => {
    const fd = new FormData()
    fd.set('collection_id', opts.collection_id)
    fd.set('health_status', opts.health_status)
    const files = Array.isArray(input) ? input : [input]
    const isArchive = files.length === 1 && /\.(zip|tar\.gz|tgz|tar)$/i.test(files[0].name)
    if (isArchive) {
      fd.append('file', files[0])
    } else {
      for (const f of files) fd.append('files', f)
    }
    if (opts.modalities?.length) fd.append('modalities', JSON.stringify(opts.modalities))
    if (opts.body_parts?.length) fd.append('body_parts', JSON.stringify(opts.body_parts))
    // JSON, exactly like `modalities` above: a study declares zero or more
    // anomalies. The gateway still accepts a bare string for the SDK's sake.
    if (opts.disease?.length) fd.append('disease', JSON.stringify(opts.disease))
    return multipartRequest('/ingest/dicom', fd, onProgress)
  },

  ingestNifti: (
    file: File,
    opts: {
      collection_id: string
      health_status: HealthStatus
      modality_hint?: string
      patient_id_hint?: string
      rescale_slope?: number
      rescale_intercept?: number
      modalities?: string[]
      body_parts?: string[]
      disease?: string[]
    },
    onProgress?: UploadProgress,
  ): Promise<{ workflow_id: string; nifti_s3_key: string; warnings?: string[] }> => {
    const fd = new FormData()
    fd.set('collection_id', opts.collection_id)
    fd.set('health_status', opts.health_status)
    fd.append('file', file)
    if (opts.modality_hint) fd.append('modality_hint', opts.modality_hint)
    if (opts.patient_id_hint) fd.append('patient_id_hint', opts.patient_id_hint)
    if (opts.rescale_slope !== undefined) fd.append('rescale_slope', String(opts.rescale_slope))
    if (opts.rescale_intercept !== undefined) fd.append('rescale_intercept', String(opts.rescale_intercept))
    if (opts.modalities?.length) fd.append('modalities', JSON.stringify(opts.modalities))
    if (opts.body_parts?.length) fd.append('body_parts', JSON.stringify(opts.body_parts))
    // JSON, exactly like `modalities` above: a study declares zero or more
    // anomalies. The gateway still accepts a bare string for the SDK's sake.
    if (opts.disease?.length) fd.append('disease', JSON.stringify(opts.disease))
    return multipartRequest('/ingest/nifti', fd, onProgress)
  },

  // ---- Collections ----------------------------------------------------------
  listCollections: (params?: {
    q?: string
    include_deleted?: boolean
    type?: CollectionType
  }): Promise<Collection[]> => {
    const qs = new URLSearchParams()
    if (params?.q) qs.set('q', params.q)
    if (params?.include_deleted) qs.set('include_deleted', 'true')
    if (params?.type) qs.set('type', params.type)
    const s = qs.toString() ? `?${qs}` : ''
    return request<Collection[]>(`/collections${s}`)
  },
  getCollection: (id: string): Promise<Collection> =>
    request<Collection>(`/collections/${id}`),
  createCollection: (body: NewCollection): Promise<Collection> =>
    request<Collection>('/collections', { method: 'POST', body: JSON.stringify(body) }),
  updateCollection: (id: string, patch: CollectionPatch): Promise<Collection> =>
    request<Collection>(`/collections/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  /** Cascades: every study in the collection is purged too. `name` is what the
   *  confirm phrase is built from, so it must be the collection's current name. */
  deleteCollection: (id: string, name: string): Promise<{ studies_purged: number }> =>
    request(
      `/collections/${encodeURIComponent(id)}` +
        `?confirm=${encodeURIComponent(`delete ${name}`)}`,
      { method: 'DELETE' },
    ),
  resetCollection: (id: string, name: string): Promise<{ studies_reset: number }> =>
    request(
      `/collections/${encodeURIComponent(id)}/reset` +
        `?confirm=${encodeURIComponent(`reset ${name}`)}`,
      { method: 'POST' },
    ),
  /** Start every `registered` study in a collection. One call, not a client
   *  loop — a project collection can hold hundreds of studies. */
  startCollection: (
    id: string,
    jobDescription?: string,
    profileId?: string,
  ): Promise<BulkStartResult> =>
    request<BulkStartResult>(`/collections/${id}/start`, {
      method: 'POST',
      // Both are stored on the collection too, so studies the auto-start sweep picks up
      // later carry the same brief AND the same workflow instead of arriving with none
      // and silently running the platform default.
      body: JSON.stringify({
        job_description: jobDescription ?? null,
        profile_id: profileId ?? null,
      }),
    }),
  stopCollectionAutoStart: (id: string): Promise<{ ok: boolean }> =>
    request<{ ok: boolean }>(`/collections/${id}/stop-auto-start`, {
      method: 'POST',
      body: '{}',
    }),
  moveCollectionStudies: (id: string, target_id: string): Promise<{ moved: number }> =>
    request<{ moved: number }>(`/collections/${id}/move-studies`, {
      method: 'POST',
      body: JSON.stringify({ target_id }),
    }),
  reassignStudy: (study_uid: string, collection_id: string): Promise<void> =>
    request<void>(`/studies/${encodeURIComponent(study_uid)}/collection`, {
      method: 'POST',
      body: JSON.stringify({ collection_id }),
    }),
  reassignStudiesBulk: (study_uids: string[], collection_id: string): Promise<{ moved: number }> =>
    request<{ moved: number }>('/studies/collection/bulk', {
      method: 'POST',
      body: JSON.stringify({ study_uids, collection_id }),
    }),

  // ---- QA sampling ----
  previewQASample: (collectionId: string, percentage: number): Promise<QASamplePreview> =>
    request<QASamplePreview>(`/collections/${collectionId}/qa-sample/preview`, {
      method: 'POST',
      body: JSON.stringify({ percentage }),
    }),

  runQASample: (collectionId: string, percentage: number): Promise<QASampleResult> =>
    request<QASampleResult>(`/collections/${collectionId}/qa-sample`, {
      method: 'POST',
      body: JSON.stringify({ percentage }),
    }),

  getIngestStatus: (workflowId: string): Promise<IngestStatus> =>
    request<IngestStatus>(`/ingest/status/${workflowId}`),

  listIngestNeedsReview: (): Promise<IngestPendingReviewItem[]> =>
    request<IngestPendingReviewItem[]>('/ingest/needs-review'),

  reviewIngestion: (workflowId: string, approved: boolean, notes?: string): Promise<{ ok: boolean }> =>
    request<{ ok: boolean }>(`/ingest/review/${workflowId}`, {
      method: 'POST',
      body: JSON.stringify({ approved, notes }),
    }),

  // --- Pre-annotation QC checklist -----------------------------------------
  //
  // The *filling in* of a checklist happens in the OHIF tab, not here: that
  // panel has its own fetch layer (`extension-tagging/src/preflight.js`) because
  // it is a different bundle and cannot import this module. What the shell needs
  // is the two read paths — the submit gate reads `description_required`, and
  // the decide tab adjudicates an artefact reject — so only those are here.
  // Duplicating the POST would give us two spellings of one write.

  /** `GET /checklists/tasks/{id}` — used by the submit gate, which must not let a
   *  radiologist file a read whose mandatory findings write-up is still empty. */
  getChecklistState: (taskId: string): Promise<ChecklistState> =>
    request<ChecklistState>(`/checklists/tasks/${encodeURIComponent(taskId)}`),

  /** `GET /checklists/artifact/{id}` — the technician's answers behind an
   *  `artifact_review` row. */
  getArtifactReview: (taskId: string): Promise<ArtifactReviewContext> =>
    request<ArtifactReviewContext>(`/checklists/artifact/${encodeURIComponent(taskId)}`),

  /**
   * A radiologist's verdict on a technician's artefact reject.
   *
   * `confirm` quarantines the study through the mask workflow — terminal.
   * `override` sends it back to the technician, who must now edit it: the
   * study's one artefact reject is already spent, so the reject button never
   * comes back. The gateway 422s an override with no remark, because that remark
   * is the only explanation the technician will ever see.
   */
  decideArtifact: (
    taskId: string,
    decision: 'confirm' | 'override',
    remark?: string,
  ): Promise<{ ok: boolean; study_uid: string; decision: string }> =>
    request(`/checklists/artifact/${encodeURIComponent(taskId)}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, remark }),
    }),

  // ---- Workflow profiles (admin + maintainer) --------------------------------
  //
  // A profile is a pipeline shape. Editing one never affects a study already
  // running: `POST /studies/{uid}/start` snapshots the graph onto the run, so the
  // run view below reads that snapshot rather than the profile.

  /** Every vocabulary the editor renders its dropdowns from.
   *
   *  Read from the server, like `AutoSegParamsCard` does with the auto-seg
   *  registry, so a new field kind or assignment policy cannot drift from what
   *  the validator accepts. Don't hand-copy it into TS. */
  getWorkflowSchema: (): Promise<WorkflowEditorSchema> =>
    request<WorkflowEditorSchema>('/workflow-profiles/schema'),

  listWorkflowProfiles: (includeArchived = false): Promise<WorkflowProfile[]> =>
    request<WorkflowProfile[]>(
      `/workflow-profiles${includeArchived ? '?include_archived=true' : ''}`,
    ),

  getWorkflowProfile: (id: string): Promise<WorkflowProfile> =>
    request<WorkflowProfile>(`/workflow-profiles/${encodeURIComponent(id)}`),

  /** 422s an unrunnable graph with `detail.errors` — a LIST, every problem at
   *  once, so the canvas shows them together instead of one per round-trip. */
  createWorkflowProfile: (body: {
    name: string
    description?: string | null
    graph: WorkflowGraph
  }): Promise<WorkflowProfile> =>
    request<WorkflowProfile>('/workflow-profiles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateWorkflowProfile: (
    id: string,
    body: { name?: string; description?: string | null; graph?: WorkflowGraph },
  ): Promise<WorkflowProfile> =>
    request<WorkflowProfile>(`/workflow-profiles/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** Soft delete. 409s while it is the default — a start that names no profile
   *  (the auto-start sweep, the SDK) resolves the default, so the last fallback
   *  cannot be archived. */
  archiveWorkflowProfile: (id: string): Promise<void> =>
    request<void>(`/workflow-profiles/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  setDefaultWorkflowProfile: (id: string): Promise<WorkflowProfile> =>
    request<WorkflowProfile>(`/workflow-profiles/${encodeURIComponent(id)}/default`, {
      method: 'POST',
    }),

  /** The graph a study is ACTUALLY running, with each node's live task rows.
   *  404 when the study was started before workflow profiles existed. */
  getWorkflowRun: (studyUid: string): Promise<WorkflowRun> =>
    request<WorkflowRun>(`/workflow-runs/${encodeURIComponent(studyUid)}`),
}
